import { NvButton } from '@simwood/nv-react-components';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContactImage from '../../shared/components/ContactImage';
import { handleEnterPress } from '../../shared/helpers/util';
import { contactsSearched } from '../../shared/reducers/contacts';
import { startCall, transferCall } from '../reducers/calls';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/contact.module.scss';
import { PhoneIcon, TransferIcon } from './icons';

export interface Props {
  number: string;
  name: string;
  description?: string;
  email?: string;
  id?: string;
  type: string;
  status?: string;
  useCallButton?: boolean;
  focussed?: boolean;

  extraActions?: {
    id: string;
    name: string;
    icon: JSX.Element;
    kind?:
      | 'primary'
      | 'secondary'
      | 'danger'
      | 'warning'
      | 'success'
      | 'info'
      | 'neutral';
    onClick: (id: string) => void;
  }[];

  expanded?: boolean;
  expand?: () => void;
  collapse?: () => void;
}

interface ExtraProps {
  callClick: () => void;
  transferClick: (transferCallId?: string) => void;
  isTransferring: boolean;
  transferCallId?: string;
  hasGrantedAudio: boolean;
}

const baseActions: {
  icon: JSX.Element;
  onClick: (props: Props & ExtraProps) => void;
  isVisible: (props: Props & ExtraProps) => boolean;
}[] = [
  {
    icon: <PhoneIcon />,
    onClick: (props) => {
      props.callClick();
      props.collapse?.();
    },
    isVisible: (props) => !props.isTransferring,
  },
  {
    icon: <TransferIcon />,
    onClick: (props) => {
      props.transferClick(props.transferCallId);
      props.collapse?.();
    },
    isVisible: (props) => props.isTransferring,
    // }, {
    //   icon: <InfoIcon />,
    //   onClick: () => {

    //   },
    //   isVisible: props => props.type === 'phonebookentry'
  },
];

// Add a 'key' prop
const actions = baseActions.map((a, i) => ({ ...a, key: i }));

const Contact: React.FC<Props> = (props) => {
  const {
    number,
    name,
    description = '',
    email = '',
    id = '',
    type,
    status = '',
    useCallButton = false,
    focussed = false,
    expanded = false,
    expand = () => {},
    collapse = () => {},
    extraActions = [],
  } = props;

  const dispatch = useDispatch<StoreDispatch>();
  // Contact-click is a blind transfer, so only for an armed inbound leg.
  const transferringCall = useSelector((state: StoreState) =>
    state.calls.find(
      (c) => c.transferInitiated === true && c.direction === 'IN',
    ),
  );
  const isTransferring = !!transferringCall;
  const transferCallId = transferringCall?.id;
  const hasGrantedAudio = useSelector(
    (state: StoreState) => state.audio.hasGrantedAudio,
  );
  const callClick = useCallback(() => {
    dispatch(startCall({ number, type, name, description, email }));
    dispatch(contactsSearched({ searchString: '' }));
  }, [description, dispatch, email, name, number, type]);
  const transferClick = useCallback(
    (transferCallId?: string) => {
      dispatch(transferCall({ id: transferCallId, number }));
      dispatch(contactsSearched({ searchString: '' }));
    },
    [dispatch, number],
  );
  const extraProps = useMemo(
    () => ({
      ...props,
      isTransferring,
      transferCallId,
      hasGrantedAudio,
      callClick,
      transferClick,
    }),
    [
      callClick,
      hasGrantedAudio,
      isTransferring,
      props,
      transferCallId,
      transferClick,
    ],
  );
  const containerClass = useCallButton
    ? styles.container
    : styles.container_clickable;
  const focussedClass = focussed ? styles.focussed : '';
  const expandedClass = expanded ? styles.container_expanded : '';
  const disabled = hasGrantedAudio ? '' : styles.disabled;
  const liClass = `${containerClass} ${focussedClass} ${expandedClass} ${disabled}`;

  // If visible is defined and false, strip out
  const visibleActions = actions.filter(
    (a) => !(a.isVisible && !a.isVisible(extraProps)),
  );

  const visibleActionsCount = visibleActions.length;

  const actionsMenu = (
    <ul className={styles.actions_row}>
      {visibleActions.map((a) => (
        <li key={a.key}>
          <div
            className={styles.action}
            role='button'
            tabIndex={0}
            onClick={() => a.onClick(extraProps)}
            onKeyDown={handleEnterPress(() => a.onClick(extraProps))}
          >
            {a.icon}
          </div>
        </li>
      ))}
    </ul>
  );

  const onCallClick = useCallback(() => {
    if (!useCallButton) {
      if (isTransferring) {
        transferClick(transferCallId);
      } else {
        callClick();
      }
    }
  }, [callClick, isTransferring, transferCallId, transferClick, useCallButton]);

  const onContactClick = useCallback(() => {
    if (expanded) {
      collapse();
    } else if (visibleActionsCount === 1) {
      // If there's only one useable action in there, just do it
      visibleActions[0].onClick(extraProps);
    } else {
      expand();
    }
  }, [
    collapse,
    expand,
    expanded,
    extraProps,
    visibleActions,
    visibleActionsCount,
  ]);

  return (
    <li>
      <div
        className={liClass}
        role='button'
        tabIndex={0}
        onClick={onCallClick}
        onKeyDown={handleEnterPress(onCallClick)}
      >
        <div
          className={styles.contact_row}
          role='button'
          tabIndex={0}
          onClick={onContactClick}
          onKeyDown={handleEnterPress(onContactClick)}
        >
          <ContactImage email={email} id={id} type={type} status={status} />
          <div className={styles.info}>
            <span className={styles.name}>{name}</span>
            <span className={styles.description}>{description}</span>
          </div>
          <div className={styles.actions}>
            {extraActions.map((action) => (
              <NvButton
                mini
                rounded
                size='small'
                key={action.id}
                kind={action.kind || 'primary'}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(id);
                }}
              >
                {action.icon}
              </NvButton>
            ))}
          </div>
        </div>
        {expanded && actionsMenu}
      </div>
    </li>
  );
};

export default Contact;
